import { Loader2, Euro, FileText, Percent, CreditCard, Key, Save, Plus, Edit, ShieldAlert, CheckCircle, Target, Monitor } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { projectTypeLabels } from "./labels";
import { PushNotificationsCard } from "@/components/PushNotificationsCard";

export const AnalyticsTab = ({ analytics, analyticsLoading, revenueGoal, goalInput, setGoalInput, handleSaveRevenueGoal }) => (
  <div className="space-y-6" data-testid="analytics-tab-content">
    {analyticsLoading ? (
      <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>
    ) : analytics ? (
      <>
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="revenue-goal-card">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center"><Target className="w-5 h-5 text-orange-600" /></div>
              <h3 className="font-heading font-semibold text-slate-900">Objectif mensuel de revenus</h3>
            </div>
            <div className="flex items-center gap-2">
              <Input type="number" min="0" value={goalInput} onChange={(e) => setGoalInput(e.target.value)} placeholder="Ex: 500" className="w-32" data-testid="revenue-goal-input" />
              <span className="text-sm text-slate-500">€</span>
              <Button size="sm" onClick={handleSaveRevenueGoal} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="save-revenue-goal-button">
                <Save className="w-4 h-4 mr-1" />Enregistrer
              </Button>
            </div>
          </div>
          {revenueGoal > 0 ? (() => {
            const pct = Math.min((analytics.monthly_revenue / revenueGoal) * 100, 100);
            const reached = analytics.monthly_revenue >= revenueGoal;
            return (
              <div data-testid="revenue-goal-progress">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">{analytics.monthly_revenue?.toFixed(2)} € / {revenueGoal.toFixed(2)} €</span>
                  <span className={`text-sm font-bold ${reached ? "text-green-600" : "text-orange-600"}`} data-testid="revenue-goal-percent">{((analytics.monthly_revenue / revenueGoal) * 100).toFixed(0)}%</span>
                </div>
                <div className="bg-slate-100 rounded-full h-4 overflow-hidden">
                  <div className={`h-full rounded-full transition-all duration-700 ${reached ? "bg-green-500" : "bg-orange-500"}`} style={{ width: `${Math.max(pct, 2)}%` }} />
                </div>
                {reached && (<p className="text-sm text-green-600 font-medium mt-2 flex items-center gap-1"><CheckCircle className="w-4 h-4" />Objectif atteint ce mois-ci, félicitations !</p>)}
              </div>
            );
          })() : (
            <p className="text-sm text-slate-400" data-testid="revenue-goal-empty">Définissez un objectif mensuel pour suivre votre progression.</p>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center"><Euro className="w-5 h-5 text-green-600" /></div>
              <span className="text-sm text-slate-500">Revenus ce mois</span>
            </div>
            <p className="text-2xl font-heading font-bold text-slate-900" data-testid="analytics-monthly-revenue">{analytics.monthly_revenue?.toFixed(2)} €</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"><FileText className="w-5 h-5 text-blue-600" /></div>
              <span className="text-sm text-slate-500">Leads ce mois</span>
            </div>
            <p className="text-2xl font-heading font-bold text-slate-900" data-testid="analytics-leads-month">{analytics.leads_this_month}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center"><Percent className="w-5 h-5 text-orange-600" /></div>
              <span className="text-sm text-slate-500">Taux de conversion</span>
            </div>
            <p className="text-2xl font-heading font-bold text-slate-900" data-testid="analytics-conversion-rate">{analytics.conversion_rate}%</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center"><CreditCard className="w-5 h-5 text-purple-600" /></div>
              <span className="text-sm text-slate-500">Leads achetés</span>
            </div>
            <p className="text-2xl font-heading font-bold text-slate-900" data-testid="analytics-purchased-leads">{analytics.purchased_leads}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="analytics-leads-family">
          <h3 className="font-heading font-semibold text-slate-900 mb-4">Demandes par famille de travaux</h3>
          {analytics.leads_by_family?.length > 0 ? (
            <div className="space-y-3">
              {analytics.leads_by_family.map((f, i) => {
                const maxCount = Math.max(...analytics.leads_by_family.map(x => x.count), 1);
                const pct = (f.count / maxCount) * 100;
                return (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-sm text-slate-600 w-56 shrink-0 truncate" title={f.label}>{f.label}</span>
                    <div className="flex-1 bg-slate-100 rounded-full h-5 relative overflow-hidden">
                      <div className="bg-blue-500 h-full rounded-full transition-all duration-500" style={{ width: `${Math.max(pct, 2)}%` }} />
                    </div>
                    <span className="text-sm font-semibold text-slate-900 w-10 text-right">{f.count}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-4">Aucune donnée</p>
          )}
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-heading font-semibold text-slate-900 mb-4">Revenus mensuels (6 derniers mois)</h3>
          <div className="space-y-3" data-testid="analytics-revenue-chart">
            {analytics.revenue_by_month?.map((m, i) => {
              const maxRev = Math.max(...analytics.revenue_by_month.map(r => r.revenue), 1);
              const pct = (m.revenue / maxRev) * 100;
              return (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-sm text-slate-500 w-20 shrink-0">{m.month}</span>
                  <div className="flex-1 bg-slate-100 rounded-full h-6 relative overflow-hidden">
                    <div className="bg-orange-500 h-full rounded-full transition-all duration-500" style={{ width: `${Math.max(pct, 2)}%` }} />
                  </div>
                  <span className="text-sm font-medium text-slate-700 w-24 text-right">{m.revenue?.toFixed(2)} €</span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="font-heading font-semibold text-slate-900 mb-4">Leads par catégorie</h3>
            <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="analytics-leads-category">
              {analytics.leads_by_category?.map((c, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <span className="text-sm text-slate-700">{projectTypeLabels[c.category] || c.category}</span>
                  <span className="text-sm font-semibold text-slate-900 bg-slate-100 px-2 py-0.5 rounded">{c.count}</span>
                </div>
              ))}
              {(!analytics.leads_by_category || analytics.leads_by_category.length === 0) && (<p className="text-sm text-slate-400 text-center py-4">Aucune donnée</p>)}
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="font-heading font-semibold text-slate-900 mb-4">Top entrepreneurs</h3>
            <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="analytics-top-contractors">
              {analytics.top_contractors?.map((c, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{c.company_name}</p>
                    <p className="text-xs text-slate-500">{c.email}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-green-600">{c.total_spent?.toFixed(2)} €</p>
                    <p className="text-xs text-slate-500">{c.leads_bought} lead{c.leads_bought > 1 ? 's' : ''}</p>
                  </div>
                </div>
              ))}
              {(!analytics.top_contractors || analytics.top_contractors.length === 0) && (<p className="text-sm text-slate-400 text-center py-4">Aucune donnée</p>)}
            </div>
          </div>
        </div>
      </>
    ) : (<p className="text-center text-slate-400 py-12">Impossible de charger les analytics</p>)}
  </div>
);

export const PricingTab = (p) => (
  <div className="space-y-6" data-testid="pricing-tab-content">
    <div className="flex items-center justify-between">
      <div>
        <h2 className="font-heading text-xl font-semibold text-slate-900">Prix des leads par catégorie</h2>
        <p className="text-sm text-slate-500 mt-1">Définissez le prix de vente d'un lead pour chaque type de projet</p>
      </div>
      <div className="flex gap-2">
        {p.leadPricing.length === 0 && (
          <Button onClick={p.handleSeedPricing} variant="outline" className="gap-2" data-testid="seed-pricing-button"><Plus className="w-4 h-4" />Initialiser prix par défaut</Button>
        )}
        {p.leadPricing.length > 0 && !p.isEditingPricing && (
          <Button onClick={() => p.setIsEditingPricing(true)} className="bg-orange-500 hover:bg-orange-600 text-white gap-2" data-testid="edit-all-pricing-button"><Edit className="w-4 h-4" />Modifier les prix</Button>
        )}
        {p.isEditingPricing && (
          <>
            <Button onClick={p.handleSaveAllPricing} className="bg-green-500 hover:bg-green-600 text-white gap-2" data-testid="save-all-pricing-button"><Save className="w-4 h-4" />Sauvegarder tout</Button>
            <Button onClick={() => { p.setIsEditingPricing(false); p.fetchPricing(); }} variant="outline" data-testid="cancel-pricing-button">Annuler</Button>
          </>
        )}
      </div>
    </div>
    {p.pricingLoading ? (
      <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>
    ) : p.leadPricing.length === 0 ? (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <Euro className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500 mb-4">Aucun prix de lead configuré.</p>
        <Button onClick={p.handleSeedPricing} className="bg-orange-500 hover:bg-orange-600 text-white">Initialiser les prix par défaut</Button>
      </div>
    ) : (
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow><TableHead className="w-[300px]">Catégorie</TableHead><TableHead className="text-right">Prix du lead</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {p.leadPricing.map((x) => (
              <TableRow key={x.category} data-testid={`pricing-row-${x.category}`}>
                <TableCell className="font-medium">{projectTypeLabels[x.category] || x.category}</TableCell>
                <TableCell className="text-right">
                  {p.isEditingPricing ? (
                    <div className="flex items-center justify-end gap-2">
                      <Input type="number" step="0.50" min="0" value={p.editingPrices[x.category] ?? x.price}
                        onChange={(e) => p.setEditingPrices(prev => ({ ...prev, [x.category]: e.target.value }))}
                        className="w-24 text-right" data-testid={`price-input-${x.category}`} />
                      <span className="text-slate-500 font-medium">€</span>
                    </div>
                  ) : (
                    <span className="text-lg font-semibold text-green-600" data-testid={`price-display-${x.category}`}>{Number(x.price).toFixed(2)} €</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {p.leadPricing.length > 0 && p.leadPricing[0].updated_at && (
          <div className="px-4 py-3 bg-slate-50 border-t border-slate-200">
            <p className="text-xs text-slate-400">Dernière mise à jour : {new Date(p.leadPricing[0].updated_at).toLocaleDateString('fr-BE')}</p>
          </div>
        )}
      </div>
    )}
  </div>
);

export const SettingsTab = (p) => (
  <>
  <PushNotificationsCard scope="admin" />
  <div className="bg-white rounded-xl border border-slate-200 p-8" data-testid="settings-panel">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center"><Key className="w-6 h-6 text-slate-600" /></div>
      <div>
        <h2 className="font-heading text-xl font-semibold text-slate-900">Paramètres d'accès</h2>
        <p className="text-sm text-slate-500">Connexion administrateur par e-mail sécurisé</p>
      </div>
    </div>
    <div className="max-w-xl space-y-4">
      <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-600">
        La connexion administrateur se fait désormais sans code : saisissez votre e-mail sur la page de connexion, puis confirmez via le lien <strong>« Oui, c'est moi »</strong> reçu par e-mail.
      </div>
      <div>
        <Label htmlFor="admin_email" className="text-slate-700 mb-2 block">Adresse e-mail administrateur</Label>
        <Input id="admin_email" type="email" value={p.adminSettings.admin_email}
          onChange={(e) => p.setAdminSettings(prev => ({ ...prev, admin_email: e.target.value }))}
          placeholder="vous@exemple.com" data-testid="settings-admin-email" />
        <p className="text-xs text-slate-500 mt-1">Le lien de connexion est envoyé à cette adresse. Assurez-vous d'y avoir accès.</p>
      </div>
    </div>
    <div className="mt-8 pt-6 border-t border-slate-200 flex justify-end">
      <Button onClick={p.handleSaveSettings} disabled={p.savingSettings} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="save-settings-button">
        {p.savingSettings ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enregistrement...</>) : (<><Save className="w-4 h-4 mr-2" />Enregistrer les modifications</>)}
      </Button>
    </div>
  </div>
  </>
);

const parseDevice = (ua) => {
  if (!ua) return "Appareil inconnu";
  const os = /Windows/i.test(ua) ? "Windows" : /iPhone|iPad|iOS/i.test(ua) ? "iOS" : /Android/i.test(ua) ? "Android" : /Mac OS X|Macintosh/i.test(ua) ? "macOS" : /Linux/i.test(ua) ? "Linux" : "";
  const browser = /Edg/i.test(ua) ? "Edge" : /Chrome/i.test(ua) ? "Chrome" : /Firefox/i.test(ua) ? "Firefox" : /Safari/i.test(ua) ? "Safari" : "Navigateur";
  return [browser, os].filter(Boolean).join(" · ") || "Appareil inconnu";
};

export const SecurityTab = ({ emailStats, securityEvents, adminLogins = [] }) => (
  <>
    {emailStats && (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="email-stats-cards">
        <div className="stats-card">
          <p className="text-sm text-slate-500 mb-1">Emails (7 derniers jours)</p>
          <p className="text-3xl font-heading font-bold text-slate-900" data-testid="email-stats-week">{emailStats.total_week}</p>
        </div>
        <div className="stats-card">
          <p className="text-sm text-slate-500 mb-1">Emails (30 derniers jours)</p>
          <p className="text-3xl font-heading font-bold text-slate-900" data-testid="email-stats-month">{emailStats.total_month}</p>
        </div>
        <div className="stats-card col-span-2">
          <p className="text-sm text-slate-500 mb-2">Répartition cette semaine</p>
          {emailStats.by_category.length === 0 ? (
            <p className="text-sm text-slate-400">Aucun email envoyé cette semaine</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {emailStats.by_category.map((c) => (<span key={c.category} className="badge bg-slate-100 text-slate-700">{c.category} : {c.count}</span>))}
            </div>
          )}
        </div>
      </div>
    )}
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="security-events-table">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-3">
        <ShieldAlert className="w-5 h-5 text-orange-500" />
        <div>
          <h3 className="font-heading font-semibold text-slate-900">Historique de sécurité (30 derniers jours)</h3>
          <p className="text-sm text-slate-500">Blocages de comptes après 5 tentatives de connexion échouées. Une alerte email vous est envoyée à chaque blocage.</p>
        </div>
      </div>
      {securityEvents.length === 0 ? (
        <div className="text-center py-16" data-testid="security-events-empty">
          <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" />
          <p className="text-slate-600 font-medium">Aucune activité suspecte détectée</p>
          <p className="text-sm text-slate-400 mt-1">Aucun blocage de compte au cours des 30 derniers jours</p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Compte ciblé</TableHead><TableHead>Adresse IP</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {securityEvents.map((event) => (
              <TableRow key={event.id} data-testid={`security-event-${event.id}`}>
                <TableCell className="text-sm text-slate-600">{new Date(event.created_at).toLocaleString("fr-BE")}</TableCell>
                <TableCell><span className="badge bg-red-100 text-red-700">Blocage (15 min)</span></TableCell>
                <TableCell className="font-medium text-slate-900">{event.account === "admin-code" ? "Code d'accès admin" : event.account}</TableCell>
                <TableCell className="font-mono text-sm text-slate-600">{event.ip}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mt-6" data-testid="admin-logins-table">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-3">
        <Monitor className="w-5 h-5 text-slate-700" />
        <div>
          <h3 className="font-heading font-semibold text-slate-900">Journal des connexions administrateur</h3>
          <p className="text-sm text-slate-500">Historique des connexions confirmées à l'espace admin. Une alerte email vous est envoyée à chaque connexion depuis une nouvelle adresse IP.</p>
        </div>
      </div>
      {adminLogins.length === 0 ? (
        <div className="text-center py-16" data-testid="admin-logins-empty">
          <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" />
          <p className="text-slate-600 font-medium">Aucune connexion enregistrée</p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow><TableHead>Date</TableHead><TableHead>Appareil</TableHead><TableHead>Adresse IP</TableHead><TableHead>Statut</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {adminLogins.map((entry) => (
              <TableRow key={entry.id} data-testid={`admin-login-${entry.id}`}>
                <TableCell className="text-sm text-slate-600">{new Date(entry.created_at).toLocaleString("fr-BE")}</TableCell>
                <TableCell className="text-sm text-slate-700">{parseDevice(entry.user_agent)}</TableCell>
                <TableCell className="font-mono text-sm text-slate-600">{entry.ip}</TableCell>
                <TableCell>
                  {entry.new_ip
                    ? <span className="badge bg-orange-100 text-orange-700">Nouvelle IP</span>
                    : <span className="badge bg-slate-100 text-slate-600">Connue</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  </>
);
